import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { Loader } from "@jti/core-components/components/Loader";
import { newrelic } from "@jti/fe-clients";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { CheckoutV2LoyaltyChoice, checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import { useGetCustomerCartIdQuery, useSetPaymentMethodOnCartMutation } from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui";

import { LoyaltyPointsContainer } from "@checkout-v2/features";
import { useCheckoutFastFlowContext } from "@checkout-v2/features/checkoutFastFlow";
import { useCartItems, useGtmPaymentInfo } from "@checkout-v2/shared/hooks";
import { useCartFreeOfCharge } from "@checkout-v2/shared/hooks/useCartFreeOfCharge/useCartFreeOfCharge";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";

import { PaymentMethodsSection } from "../../components/PaymentMethodsSection";
import { PaymentMethodsSectionPreview } from "../../components/PaymentMethodsSectionPreview";
import { useAvailablePaymentMethodsForCart } from "../../hooks/useAvailablePaymentMethodsForCart";
import { useCartPaymentLoyaltyPreview } from "../../hooks/useCartPaymentLoyaltyPreview";
import { usePreparedPaymentOptions } from "../../hooks/usePreparedPaymentOptions";

type PaymentMethodsCartContainerProps = {
  mode?: "edit" | "view";
  onPaymentSectionEdit?: () => void;
};

const { sanitize } = dompurify;

const logError = (error: unknown) => {
  if (error instanceof Error) {
    newrelic.noticeError(error);
  } else {
    newrelic.noticeError(new Error("Unknown error in PaymentMethodsCartContainer"));
  }
};

export const PaymentMethodsCartContainer = ({ mode, onPaymentSectionEdit }: PaymentMethodsCartContainerProps) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const dispatch = useDispatch();
  const checkoutConfiguration = useCheckoutV2Configuration();

  const { evoMethods, magentoMethods, isLoading, isError, error } = useAvailablePaymentMethodsForCart();
  const { isCheckoutFastFlowInProgress, checkoutFlowType, paymentInfoPrefilled } = useCheckoutFastFlowContext();
  const { trackGtmPaymentInfo } = useGtmPaymentInfo();
  const { data: cartIdData } = useGetCustomerCartIdQuery();
  const cartId = cartIdData?.customerCart.id;
  const { data: cartItems } = useCartItems();

  const selectedShippingMethodCode = useSelector((state) => state.checkoutV2.data?.shippingMethodCode);
  const selectedPaymentMethodCode = useSelector((state) => state.checkoutV2.data?.paymentMethodCode);
  const securedFieldsPayment = useSelector((state) => state.checkoutV2.data?.securedFieldsPayment);

  const { loyaltyChoice, loyaltyCampaignId } = useSelector((state) => state.checkoutV2.loyaltyPointsState);

  const [
    setPaymentMethodOnCart,
    {
      isLoading: isSettingPaymentMethod,
      isError: isSettingPaymentMethodError,
      error: paymentMethodError,
      reset: resetSetPaymentMethodError,
    },
  ] = useSetPaymentMethodOnCartMutation();
  const { isCartFreeOfCharge } = useCartFreeOfCharge();

  const { isLoyaltyInfoVisible, loyaltyInfo } = useCartPaymentLoyaltyPreview({
    cartId,
    cartItems,
    onPaymentSectionEdit,
  });

  const paymentOptions = usePreparedPaymentOptions({
    evoMethods,
    magentoMethods,
    selectedShippingMethodCode,
    orderItems: cartItems,
  });

  const checkLoyaltyCouponsRedemption = (): boolean => {
    const isRedeemWithoutVoucher = loyaltyChoice === CheckoutV2LoyaltyChoice.REDEEM && !loyaltyCampaignId;
    if (isRedeemWithoutVoucher) {
      dispatch(checkoutV2Slice.actions.setLoyaltyError(true));
      return false;
    }

    return true;
  };

  const onPaymentSectionSubmit = async (selectedMethodCode: string, magentoPaymentMethodCode: string) => {
    if (!cartId) {
      return false;
    }

    if (!checkLoyaltyCouponsRedemption()) {
      return false;
    }

    try {
      await setPaymentMethodOnCart({ cartId, code: magentoPaymentMethodCode }).unwrap();

      trackGtmPaymentInfo(selectedMethodCode, checkoutFlowType, paymentInfoPrefilled);

      return true;
    } catch (err) {
      newrelic.noticeError(err as Error);
      return false;
    }
  };

  if (isLoading || isCheckoutFastFlowInProgress) {
    return <Loader />;
  }

  const onFreeOrderContinue = () => {
    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: "completed",
        orderConfirmationSection: "active",
      })
    );
  };

  if (mode === "view") {
    if (isCartFreeOfCharge) {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: sanitize(checkoutConfiguration.general.zeroValueOrderNotificationText),
          }}
        />
      );
    }

    return (
      <PaymentMethodsSectionPreview
        paymentOptions={paymentOptions}
        selectedPaymentMethodCode={selectedPaymentMethodCode}
        isLoyaltyInfoVisible={isLoyaltyInfoVisible}
        loyaltyInfo={loyaltyInfo}
        securedFieldsPayment={securedFieldsPayment}
      />
    );
  }

  if (isCartFreeOfCharge) {
    return (
      <>
        <PaymentMethodsSection.CartLoyaltyInfo>
          <LoyaltyPointsContainer />
        </PaymentMethodsSection.CartLoyaltyInfo>
        <div
          dangerouslySetInnerHTML={{
            __html: sanitize(checkoutConfiguration.general.zeroValueOrderNotificationText),
          }}
        />
        <Button type="button" onClick={onFreeOrderContinue}>
          {__("cart.order.payment_method.submit")}
        </Button>
      </>
    );
  }

  if (isError || paymentOptions.length < 1) {
    logError(error);

    return (
      <AlertBanner
        tone="error"
        title={__("cart.order.payment_method.no_methods.title")}
        description={__("cart.order.payment_method.no_methods.description")}
        actions={[{ href: config.CONTACT_US_URL, label: __("cart.order.payment_method.no_methods.cta") }]}
      />
    );
  }

  return (
    <>
      <PaymentMethodsSection.CartLoyaltyInfo>
        <LoyaltyPointsContainer />
      </PaymentMethodsSection.CartLoyaltyInfo>
      <PaymentMethodsSection
        paymentOptions={paymentOptions}
        selectedPaymentMethodCode={selectedPaymentMethodCode}
        resetError={resetSetPaymentMethodError}
        isError={isSettingPaymentMethodError}
        isLoading={isSettingPaymentMethod}
        error={paymentMethodError}
        onSubmit={onPaymentSectionSubmit}
      />
    </>
  );
};
